import { formatMoney, formatMoneyShort, type CostBar as Bar, type CostBarDto } from '@nsb/core';
import { useId, useLayoutEffect, useRef, useState, type ReactNode } from 'react';
import { Link, useNavigate } from 'react-router';
import { marksOf, placeMarks, scaleOf, type MarkTone } from './priceBar.ts';

/**
 * The money bar (design study 04's bar with the price taken out, 2026-10-07:
 * the estimate is stripped until it is built again on the steps). One bar for
 * NSB and the client, the same numbers, exactly to scale: billed above, money
 * in below ("ideally they meet"); billed and not yet paid is a red gap under
 * it, and money paid ahead runs on past it. Every line on it is named at its
 * own top: the budget and where its contingency begins (dotted,
 * "Contingency"), a budget waiting on an answer ("pending", which opens it).
 * With nothing billed and nothing in, it is an empty outline, never a bar of
 * noughts. Nothing is a gradient and nothing is rounded off.
 */

const HEIGHT = { standard: 14, client: 18 } as const;
type Reader = 'client' | 'staff';
const TONE: Record<MarkTone, string> = {
  ink: 'var(--color-ink)',
  'ink-2': 'var(--color-ink-2)',
  'ink-3': 'var(--color-ink-3)',
  alert: 'var(--color-alert)',
};
const FILL = {
  billed: 'var(--color-moss)',
  paid: 'var(--color-paid)',
  owed: 'var(--color-owed)',
} as const;

/** The width the bar is drawn at: its box's, followed as it changes; a fair guess where nothing is laid out (the server, tests). */
function useWidth(fallback: number) {
  const ref = useRef<HTMLDivElement>(null);
  const [width, setWidth] = useState(fallback);
  useLayoutEffect(() => {
    const el = ref.current;
    if (!el) return;
    const take = (w: number) => setWidth(Math.max(120, Math.floor(w)));
    take(el.getBoundingClientRect().width);
    const ro = new ResizeObserver(([e]) => take(e!.contentRect.width));
    ro.observe(el);
    return () => ro.disconnect();
  }, []);
  return [ref, width] as const;
}

/** The two sub-bars, clipped to the bar: billed over money in, the gap between them unpaid or paid ahead. */
function Bands({ bar, px, top, h, clip }: { bar: Bar; px: (c: number) => number; top: number; h: number; clip: string }) {
  const half = h / 2;
  return (
    <>
      <clipPath id={clip}>
        <rect x={0} y={top} width={px(bar.totalCents)} height={h} rx={2} />
      </clipPath>
      <g clipPath={`url(#${clip})`}>
        {bar.billedCents > 0 ? <rect x={0} y={top} width={px(bar.billedCents)} height={half} fill={FILL.billed} /> : null}
        {bar.moneyInCents > 0 ? <rect x={0} y={top + half} width={px(bar.moneyInCents)} height={half} fill={FILL.paid} /> : null}
        {bar.unpaidCents > 0 ? <rect x={px(bar.moneyInCents)} y={top + half} width={px(bar.unpaidCents)} height={half} fill={FILL.owed} /> : null}
        <line x1={0} x2={px(bar.totalCents)} y1={top + half} y2={top + half} stroke="var(--color-paper)" strokeWidth={1} />
      </g>
    </>
  );
}

/** The bar's outline while there is nothing to draw in it. */
function Empty({ width, top, h }: { width: number; top: number; h: number }) {
  return <rect x={0.5} y={top + 0.5} width={width - 1} height={h - 1} rx={2} fill="none" stroke="var(--color-rule-strong)" />;
}

/** What the bar says, for a screen reader: the sentence above it says the rest. */
function spoken(data: CostBarDto): string {
  const bar = data.bar;
  const parts = [bar ? `${formatMoney(bar.billedCents)} billed, ${formatMoney(bar.moneyInCents)} paid` : 'nothing billed or paid yet'];
  if (data.ceiling) parts.push(`the budget ${formatMoney(data.ceiling.totalCents)}, all in`);
  if (data.pendingBudget) parts.push(`a budget of ${formatMoney(data.pendingBudget.totalCents)} waiting on an answer`);
  return `The money bar: ${parts.join('; ')}.`;
}

/** `reader`: who reads the words under it, the client on their own home ("proposed by you"), else NSB. */
export function CostBar({
  data,
  size = 'standard',
  reader = 'staff',
  budgetHref = null,
}: {
  data: CostBarDto;
  size?: keyof typeof HEIGHT;
  reader?: Reader;
  budgetHref?: string | null;
}) {
  const [ref, width] = useWidth(640);
  const navigate = useNavigate();
  const clip = `bar-clip-${useId().replace(/[^a-zA-Z0-9]/g, '')}`;
  const bar = data.bar;
  const h = HEIGHT[size];
  const scale = scaleOf(data);
  const px = (c: number) => (Math.max(0, c) / scale) * width;
  const placed = placeMarks(marksOf(data, budgetHref), px, width);
  const rows = placed.length ? Math.max(...placed.map((m) => m.row)) + 1 : 0;
  const top = rows ? rows * 14 + 8 : 6;
  return (
    <figure>
      <div ref={ref} className="w-full">
        <svg width={width} height={top + h + 8} viewBox={`0 0 ${width} ${top + h + 8}`} role="img" aria-label={spoken(data)} className="block overflow-visible">
          {bar ? <Bands bar={bar} px={px} top={top} h={h} clip={clip} /> : <Empty width={width} top={top} h={h} />}
          {placed.map(({ mark, x, row, anchor }) => {
            const ty = top - 6 - row * 14;
            const colour = TONE[mark.tone];
            const words = mark.link && mark.text.endsWith(mark.link.word) ? mark.text.slice(0, -mark.link.word.length) : mark.text;
            const text = (
              <text x={anchor === 'start' ? x + 4 : x - 4} y={ty} fontSize={10.5} textAnchor={anchor} fill={colour} fontFamily="Inter, system-ui, sans-serif">
                {words}
                {mark.link && words !== mark.text ? (
                  <tspan textDecoration="underline" fill="var(--color-ink)">
                    {mark.link.word}
                  </tspan>
                ) : null}
              </text>
            );
            return (
              <g key={`${mark.kind}-${mark.text}`}>
                <line x1={x} x2={x} y1={ty - 8} y2={top + h + 5} stroke={colour} strokeWidth={1.5} strokeDasharray={mark.kind === 'dotted' ? '2 2' : undefined} />
                {mark.link ? (
                  <a
                    href={mark.link.href}
                    onClick={(e) => {
                      e.preventDefault();
                      navigate(mark.link!.href);
                    }}
                    className="cursor-pointer"
                  >
                    {text}
                  </a>
                ) : (
                  text
                )}
              </g>
            );
          })}
        </svg>
      </div>
      <Legend bar={bar} />
      <Under data={data} reader={reader} budgetHref={budgetHref} />
    </figure>
  );
}

/** The sub-bars named under the bar, each with its amount. Nothing billed or in: no row of noughts (guide §20.3). */
function Legend({ bar }: { bar: Bar | null }) {
  if (!bar) return null;
  const items: ReactNode[] = [<Band key="billed" fill={FILL.billed} label="Billed" cents={bar.billedCents} />, <Band key="in" fill={FILL.paid} label="Money in" cents={bar.moneyInCents} />];
  if (bar.unpaidCents) items.push(<Band key="owed" fill={FILL.owed} label="Billed, not yet paid" cents={bar.unpaidCents} alert />);
  if (bar.aheadCents) items.push(<Band key="ahead" fill={FILL.paid} label={bar.ended ? 'To give back' : 'Paid ahead'} cents={bar.aheadCents} />);
  return <figcaption className="mt-2 flex flex-wrap items-baseline gap-x-6 gap-y-1.5">{items}</figcaption>;
}

function Band({ fill, label, cents, alert = false }: { fill: string; label: string; cents: number; alert?: boolean }) {
  return (
    <span className="inline-flex items-baseline gap-2">
      <span className="inline-block h-2 w-3 rounded-[1px]" style={{ background: fill }} />
      <span className={`label ${alert ? 'text-alert' : ''}`}>{label}</span>
      <span className="num text-sm">{formatMoneyShort(cents)}</span>
    </span>
  );
}

/** A small mark before a line's words: the line's own colour and dash. */
function Tick({ colour, dotted = false }: { colour: string; dotted?: boolean }) {
  return (
    <span
      aria-hidden="true"
      className="inline-block h-3 w-0 translate-y-0.5"
      style={{ borderLeft: `1.5px ${dotted ? 'dotted' : 'solid'} ${colour}` }}
    />
  );
}

/** The lines on the bar in words: the budget, and a budget waiting on an answer, the client named as the reader is. */
function Under({ data, reader, budgetHref }: { data: CostBarDto; reader: Reader; budgetHref: string | null }) {
  const bar = data.bar;
  if (bar?.ended) return null;
  const bits: ReactNode[] = [];
  const ceiling = data.ceiling;
  if (ceiling) {
    bits.push(
      <span key="budget" className="inline-flex flex-wrap items-baseline gap-x-2">
        <Tick colour="var(--color-ink)" />
        <span className="label">Budget</span>
        <span className="num">{formatMoney(ceiling.totalCents)}</span>
        <span className="text-ink-2">
          all in, <span className="num">{formatMoneyShort(ceiling.contingencyCents)}</span> of it contingency
        </span>
      </span>,
    );
  }
  const waiting = data.pendingBudget;
  if (waiting) {
    const client = reader === 'client' ? 'you' : 'the client';
    const by = waiting.byRole === 'client' ? client : 'NSB';
    const on = waiting.byRole === 'client' ? 'NSB' : client;
    bits.push(
      <span key="waiting" className="inline-flex flex-wrap items-baseline gap-x-2">
        <Tick colour="var(--color-ink-2)" dotted />
        <span className="label">Budget</span>
        <span className="num">{formatMoney(waiting.totalCents)}</span>
        <span className="text-ink-2">
          proposed by {by}, <span className="num">{formatMoneyShort(waiting.contingencyCents)}</span> of it contingency ·{' '}
          {budgetHref ? (
            <Link to={budgetHref} className="link text-ink">
              pending
            </Link>
          ) : (
            'pending'
          )}
          , waiting on {on}
        </span>
      </span>,
    );
  }
  return bits.length ? <p className="mt-2 flex flex-wrap items-baseline gap-x-6 gap-y-1 text-sm">{bits}</p> : null;
}

/** The bar drawn small for the Projects page (design study 04, E-A): the same sub-bars, no names. */
export function MiniBar({ bar, label }: { bar: Bar; label: string }) {
  const [ref, width] = useWidth(128);
  const clip = `mini-clip-${useId().replace(/[^a-zA-Z0-9]/g, '')}`;
  const px = (c: number) => (Math.max(0, c) / Math.max(bar.totalCents, 1)) * width;
  const h = 8;
  return (
    <div ref={ref} className="w-full">
      <svg width={width} height={h + 4} viewBox={`0 0 ${width} ${h + 4}`} role="img" aria-label={label} className="block">
        <Bands bar={bar} px={px} top={2} h={h} clip={clip} />
      </svg>
    </div>
  );
}
